import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import toast from "react-hot-toast";
import {
  ActionIcon,
  Group,
  Grid,
  Flex,
  Text,
  HoverCard,
  Divider,
  Modal,
  Box,
  Select,
  List,
  Button,
} from "@mantine/core";
import { IconBolt, IconPlus, IconTrash } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export default function SelectItemManagement({ field, effortId }) {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);
  const [triggers, setTriggers] = useState([]);
  useEffect(() => {
    if (!isOpen) return;
    fetchTriggers();
  }, [isOpen]);

  if (!field) return null;

  function fetchTriggers() {
    axios
      .post(`/efforts/${effortId}/data-grabber/`, {
        context: "engagement_item_triggers",
      })
      .then(({ data }) => {
        setTriggers(data.response);
      })
      .catch((err) => {
        setTriggers([]);
      });
  }

  function onClose() {
    setOpen(false);
  }

  return (
    <div>
      <ActionIcon
        onClick={() => setOpen(true)}
        title={t("mixer:effort_builder:conditional_email_setup")}
        variant="light"
        color="green"
        radius="xl"
      >
        <IconBolt size={16} />
      </ActionIcon>
      <Modal
        title={t("mixer:effort_builder:conditional_email_setup")}
        opened={isOpen}
        onClose={onClose}
      >
        <Text mb="xl">
          {t("common:field_singular")}: <b>{field.label}</b>
        </Text>
        {field.options.map((m, i) => (
          <div key={i}>
            <SelectItem
              effortId={effortId}
              item={m}
              triggers={triggers.filter((f) => f.select_item_id === m.id)}
              fetchData={() => fetchTriggers()}
            />
            {i < field.options.length - 1 && <Divider mt="lg" mb="lg" />}
          </div>
        ))}
      </Modal>
    </div>
  );
}

function SelectItem({ item, effortId, fetchData, triggers }) {
  const [templateId, setTemplateId] = useState("");
  const [loading, setLoading] = useState(false);
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [postmarkTemplateNames, setPostmarkTemplateNames] = useState([]);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const fetchEiWithTrigger = () =>
    queryClient.invalidateQueries([`eiWithTrigger${effortId}`]);

  useEffect(() => {
    setTemplateId("");
    if (!triggerOpen) return;
    fetchTemplates();
  }, [triggerOpen]);

  if (!item) return null;

  function fetchTemplates() {
    axios
      .post(`/efforts/${effortId}/data-grabber/`, {
        context: "conditional_reg_templates",
      })
      .then(({ data }) => {
        setPostmarkTemplateNames(data.response);
      })
      .catch((err) => {
        setPostmarkTemplateNames([]);
      });
  }

  function onSubmit() {
    const req = {
      select_item_id: item.id,
      email_template: templateId,
      variety: "email_registration",
    };

    axios
      .post(`/triggers/`, req)
      .then(() => {
        setLoading(false);
        setTriggerOpen(false);
        toast.success(t("common:saved") + "!");
        fetchEiWithTrigger();
        fetchData();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  const templateOptions = postmarkTemplateNames.map((m) => ({
    label: m,
    value: m,
  }));

  return (
    <div>
      <Text size="sm">
        {t("common:option_singular")}: <b>{item.label}</b>
      </Text>
      {!triggerOpen && (
        <Box mt="sm" mb="lg">
          {triggers.map((t, i) => (
            <TriggerItem
              key={i}
              trigger={{
                ...t,
                option_label: item.label,
              }}
              fetchData={fetchData}
              onRemoveSuccess={() => {
                fetchEiWithTrigger();
              }}
            />
          ))}
        </Box>
      )}
      {!triggerOpen && triggers.length === 0 && (
        <Flex gap="sm" mt="sm">
          <Button
            size="xs"
            variant="light"
            radius="xl"
            color="gray"
            leftSection={<IconPlus size={16} />}
            onClick={() => setTriggerOpen(!triggerOpen)}
          >
            {t("mixer:effort_builder:select_template")}
          </Button>
        </Flex>
      )}
      {triggerOpen && (
        <Box mt="sm">
          <Select
            label={t(
              "mixer:effort_builder:select_email_template_on_registration_confirmation",
              { option: item.label }
            )}
            data={templateOptions}
            value={templateId}
            onChange={(e) => setTemplateId(e)}
            required
            placeholder={t("common:select_one")}
            searchable
          />
          <Button
            fullWidth
            mt="sm"
            disabled={!templateId}
            onClick={onSubmit}
            loading={loading}
          >
            {t("mixer:effort_builder:use_selected_template")}
          </Button>
          <Flex justify="center" mt="sm">
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              onClick={() => setTriggerOpen(false)}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              onClick={() => fetchTemplates()}
            >
              {t("mixer:effort_builder:refresh_templates")}
            </Button>
          </Flex>
        </Box>
      )}
    </div>
  );
}

function TriggerItem({ trigger, fetchData, onRemoveSuccess }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  function onRemoveClick() {
    setLoading(true);
    axios
      .delete(`/triggers/${trigger.id}/`)
      .then(() => {
        toast.success(t("common:deleted") + "!");
        setLoading(false);
        fetchData();
        onRemoveSuccess();
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  return (
    <Flex gap="xs" align="center">
      <ActionIcon
        loading={loading}
        size="xs"
        variant="light"
        radius="xl"
        color="red"
        onClick={onRemoveClick}
      >
        <IconTrash size={14} />
      </ActionIcon>
      <Text size="sm">
        {t("mixer:effort_builder:trigger_item_text", {
          template: trigger.email_template,
          option: trigger.option_label,
        })}
      </Text>
    </Flex>
  );
}
